import { Injectable } from "@angular/core";
import { CHService } from "../model/ch-service";
import { CHServiceMetadata } from "../store/clarehome.state";

@Injectable({
    providedIn: "root",
})
export class ServiceMetadataService {
    public static hasAction(metadata, actionType, actionName) {
        let actions = [];

        if (metadata && metadata[actionType]) {
            actions = Object.keys(metadata[actionType]);
        }

        return actions.indexOf(actionName) !== -1;
    }

    public static getActionType(metadata, actionName, cpName) {
        let theMetadata = metadata;

        if (cpName) {
            theMetadata = this.getControlPointMetadata(metadata.controlPoints, cpName);
        }

        if (this.hasAction(theMetadata, "Operations", actionName)) {
            return "operation";
        }

        if (this.hasAction(theMetadata, "Properties", actionName)) {
            return "property";
        }
        return null;
    }

    public static getActionMetadata(cpMetadata, actionName) {
        let actions = {};
        if (this.hasAction(cpMetadata, "Operations", actionName)) {
            actions = cpMetadata.Operations;
        } else if (this.hasAction(cpMetadata, "Properties", actionName)) {
            actions = cpMetadata.Properties;
        }

        return actions[actionName];
    }

    public static isEnumeratedCp(cpId) {
        return /(.*_)(\d){1,3}/.exec(cpId) || []; // NOSONAR
    }

    public static isControllable(actionMetadata) {
        return actionMetadata && !actionMetadata.hideInAppConfig && (!actionMetadata.access || actionMetadata.access.indexOf("W") !== -1);
    }

    public static isEventable(actionMetadata) {
        return actionMetadata && !actionMetadata.hideInAppConfig && (!actionMetadata.access || actionMetadata.access.indexOf("E") !== -1);
    }

    public static isOnlyEventable(actionMetadata) {
        return actionMetadata?.access === "E";
    }

    public static getDeviceWithDeviceClass(devices, deviceClass) {
        for (const uid in devices) {
            if (devices?.[uid]?.DeviceClasses && devices[uid].DeviceClasses.indexOf(deviceClass) !== -1) {
                return devices[uid];
            }
        }
    }

    public static isActionSupported(action, controlPoint, device): boolean {
        const dco = device?.DeviceClassObjects?.[controlPoint.deviceClass];
        return !!(dco && (dco.Operations?.includes(action.Name) || dco.Properties?.includes(action.Name)));
    }

    public static isActionDeprecated(action, controlPoint, device) {
        if (action?.Metadata.hasOwnProperty("deprecatedInFavorOf") && device?.DeviceClassObjects) {
            const dco = device.DeviceClassObjects[controlPoint.deviceClass];
            if (
                (dco.Operations && dco.Operations.indexOf(action.Metadata.deprecatedInFavorOf) !== -1) ||
                (dco.Properties && dco.Properties.indexOf(action.Metadata.deprecatedInFavorOf) !== -1)
            ) {
                return true;
            }
        }

        return false;
    }

    public static isActionControllable(cpMetadata, actionName?) {
        if (actionName) {
            const actionMetadata = (this.getActionMetadata(cpMetadata, actionName) || {}).Metadata;
            return this.isControllable(actionMetadata);
            // else loop over all the actions and see if any is controllable
        }

        let action;

        if (cpMetadata.Operations) {
            for (action in cpMetadata.Operations) {
                if (cpMetadata.Operations.hasOwnProperty(action) && this.isControllable(cpMetadata.Operations[action].Metadata)) {
                    return true;
                }
            }
        }

        if (cpMetadata.Properties) {
            for (action in cpMetadata.Properties) {
                if (cpMetadata.Properties.hasOwnProperty(action) && this.isControllable(cpMetadata.Properties[action].Metadata)) {
                    return true;
                }
            }
        }

        return false;
    }

    public static isActionEventable(cpMetadata, actionName?) {
        if (actionName) {
            const actionMetadata = (this.getActionMetadata(cpMetadata, actionName) || {}).Metadata;
            return this.isEventable(actionMetadata);
            // else loop over all the actions and see if any is controllable
        }

        if (cpMetadata.Properties) {
            for (const action in cpMetadata.Properties) {
                if (cpMetadata.Properties.hasOwnProperty(action) && this.isEventable(cpMetadata.Properties[action].Metadata)) {
                    return true;
                }
            }
        }

        return false;
    }

    public static getMetadata(serviceMetadata, cpName, actionName?) {
        const cpMetadata = this.getControlPointMetadata(serviceMetadata, cpName);

        if (actionName && cpMetadata) {
            return this.getActionMetadata(cpMetadata, actionName);
        }

        return cpMetadata;
    }

    public static isControlPointControllable(serviceMetadata, cpName, actionName) {
        // if actionName is set, then see if that action is controllable
        if (cpName) {
            // check this one
            const cpMetadata = this.getMetadata(serviceMetadata, cpName);
            return this.isActionControllable(cpMetadata, actionName);
        }
        // loop over the cps
        for (const controlPoint in serviceMetadata) {
            if (serviceMetadata.hasOwnProperty(controlPoint) && this.isActionControllable(serviceMetadata[controlPoint])) {
                return true;
            }
        }

        return false;
    }

    public static isControlPointEventable(serviceMetadata, cpName, actionName?) {
        // if actionName is set, then see if that action is controllable
        if (cpName) {
            // check this one
            const cpMetadata = this.getMetadata(serviceMetadata, cpName);
            return this.isActionEventable(cpMetadata, actionName);
        }
        // loop over the cps
        for (const controlPoint in serviceMetadata) {
            if (serviceMetadata.hasOwnProperty(controlPoint) && this.isActionEventable(serviceMetadata[controlPoint])) {
                return true;
            }
        }

        return false;
    }

    public static isServiceControllable(metadata, cpName, actionName) {
        if (!metadata || !metadata.controlPoints) {
            return false;
        }
        return this.isControlPointControllable(metadata.controlPoints, cpName, actionName);
    }

    public static getServiceMetadataKey(cpName) {
        const matches = this.isEnumeratedCp(cpName);

        if (matches.length) {
            return matches[1];
        }
        return cpName;
    }

    public static getControlPointMetadata(serviceMetadata, cpName) {
        const metaKey = this.getServiceMetadataKey(cpName);

        if (serviceMetadata) {
            return serviceMetadata[metaKey] || {};
        }
        return {};
    }

    public static getControlPointActionMetadataByCPName(service: CHService, metadata: CHServiceMetadata, cpName: string, action: string) {
        const controlPoints = metadata.services[service.definitionId].controlPoints;
        const cpMetadata = this.getControlPointMetadata(controlPoints, cpName);
        return this.getActionMetadata(cpMetadata, action);
    }

    /**
     * Get the metadata for a given control point action
     *
     * @param service The service which has the control point to look up
     * @param metadata The service metadata
     * @param deviceUid The device UID used to determine which control point to look up
     * @param deviceClass The device class used to identify the control point to look up
     * @param property The property or operation name
     *
     * @description When working with actions, we need to look up metadata to give the user more
     * user-friendly information about the action. We do this by looking up information in service
     * metadata. If we did not do this, we'd end up displaying something like:
     *
     * HW Heater sensor waterV2 is { eventType:["5", "int"], eventCode: ["2", "int"]}
     *
     * Instead, by looking up metadata, we can display the message as:
     *
     * HW Heater sensor Water Sensor is Tripped
     *
     * We first find the control points on the service. We match the given deviceUid and device class to
     * one of the control points on the service. If there's a match, we get its metadata, then look up
     * the specific metadata for the given property/operation.
     *
     */
    public static getControlPointActionMetadataByDeviceClass(
        service: CHService,
        metadata: CHServiceMetadata,
        deviceUid: string,
        deviceClass: string,
        property: string
    ) {
        let actionMeta;
        const matchingCP = Object.values(service.controlPoints).find(cp => cp.deviceClass === deviceClass && cp.deviceUid === deviceUid);

        if (matchingCP) {
            const matches = this.isEnumeratedCp(matchingCP.uid);
            const controlPointId = matches.length ? matches[1] : matchingCP.uid;
            const cpMetadata = metadata.services[service.definitionId].controlPoints[controlPointId];
            const action = this.getActionMetadata(cpMetadata, property);
            actionMeta = action.Metadata;
        }

        return actionMeta;
    }
}
